<script setup lang="ts">
import type { PropType } from "vue";
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import Header from "@/components/organisms/common/Header.vue";
import SmsAuthForm from "@/components/organisms/auth/SmsAuthForm.vue";
import { formatPhoneNumber } from "@/utils/formatPhoneNumber";
import BlockBack from "@/components/organisms/common/BlockBack.vue";
import { CreateSms } from "@/types/auth/CreateSms";
import { ValidateSms } from "@/types/auth/ValidateSms";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import { useModal } from "@/utils/modal";

const DEFAULT_TIMER_COUNT = 300;
const MESSAGE_ON_SMS_SENDING_ERROR =
  "SMSが送信できませんでした。時間をおいて再度お試しいただくか、音声コードによる認証をお試しください";

const props = defineProps({
  title: {
    type: String,
    required: true
  },
  phoneNumber: {
    type: String,
    default: null
  },
  createSms: {
    type: Function as PropType<CreateSms>,
    required: true
  },
  validateSms: {
    type: Function as PropType<ValidateSms>,
    required: true
  }
});
const emit = defineEmits<{
  (event: "validated", smsSessionId: string): void;
  (event: "switchToVoice"): void;
}>();

const router = useRouter();
const modal = useModal();
const loading = ref(false);

const latestPin = ref("");
const smsTimerCount = ref(0);

const phoneNumberWithHyphen = computed(() => {
  return formatPhoneNumber(props.phoneNumber);
});

const sendSms = () => {
  if (!props.phoneNumber) throw new Error();
  const body = {
    phoneNumber: props.phoneNumber
  };
  return props.createSms(body);
};

const smsValidation = (pin: string) => {
  if (!props.phoneNumber) throw new Error();
  const body = {
    latestPin: pin,
    phoneNumber: props.phoneNumber
  };
  return props.validateSms(body);
};

const setSmsTimer = () => {
  smsTimerCount.value = DEFAULT_TIMER_COUNT;
  const timer = setInterval(() => {
    if (smsTimerCount.value === 0) {
      clearInterval(timer);
      return;
    }
    smsTimerCount.value--;
  }, 1000);
};

const onClickResendSms = async () => {
  if (smsTimerCount.value !== 0) return;

  try {
    loading.value = true;
    await sendSms();
    setSmsTimer();
  } catch {
    await modal.msgBoxOk(MESSAGE_ON_SMS_SENDING_ERROR, {
      centered: true
    });
  } finally {
    loading.value = false;
  }
};

const onInputValidateSmsAndTransition = async (pin: string) => {
  if (pin === null) return;
  if (pin.length !== 4) return;

  try {
    loading.value = true;
    const { isVerified, smsSessionId } = await smsValidation(pin);
    if (!isVerified) return;
    emit("validated", smsSessionId);
  } catch {
    await modal.msgBoxOk("コードが正しくありません", {
      centered: true
    });
    latestPin.value = "";
  } finally {
    loading.value = false;
  }
};

const onClickSwitchVoiceCall = async (): Promise<void> => {
  const result = await modal.msgBoxConfirm(
    `自動の音声ガイダンスが流れ、4桁の数字を読み上げますので、お手元にメモをご用意の上、以下のボタンを押してください`,
    {
      bodyClass: "text-primary-dark",
      okTitle: "電話を受ける",
      cancelTitle: "キャンセル",
      cancelVariant: "gray-03",
      centered: true
    }
  );

  if (result) {
    emit("switchToVoice");
  }
};

onMounted(async () => {
  try {
    loading.value = true;
    await sendSms();
    setSmsTimer();
  } catch {
    await modal.msgBoxOk(MESSAGE_ON_SMS_SENDING_ERROR, {
      centered: true
    });
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div>
    <Header>
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="router.back()" />
      </template>
      {{ title }}
    </Header>
    <main class="container">
      <div class="description" data-testid="description">
        <strong>{{ phoneNumberWithHyphen }}</strong
        >に<br />
        送信された4桁の認証コードを<br />
        入力しましょう
      </div>
      <SmsAuthForm
        v-model="latestPin"
        @input="onInputValidateSmsAndTransition"
      />
      <div class="text-size-14 text-center">
        <span
          :class="{ 'text-primary-dark': smsTimerCount === 0 }"
          @click="onClickResendSms"
          data-testid="reSend"
        >
          認証コードを再送信（{{ smsTimerCount }}秒）
        </span>
      </div>

      <div class="text-size-14 text-center">
        <p class="mb-1">SMSによる認証コードが届かない場合</p>
        <span
          class="link"
          @click="onClickSwitchVoiceCall"
          data-testid="toSwitchVoiceCall"
        >
          音声通話により認証コードを取得する
        </span>
      </div>
    </main>
    <BlockBack />
  </div>
</template>

<style lang="scss" scoped>
main {
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;
  padding: 0 24px;
  margin: 80px 0 32px;
  color: #40220f;
}

.description {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
  text-align: center;
}

.container {
  p,
  span {
    color: #40220f;
  }
}

.link {
  text-decoration: underline;
  cursor: pointer;
}
</style>
